import { Option } from './option.interface';
import { SelectActions } from './select-actions';

/**
 * Check if the selected option is in view, and scroll if not
 */
export const updateScroll = (element: HTMLElement) => {
  const scrollBottom = element.clientHeight + element.scrollTop;
  const elementBottom = element.offsetTop + element.offsetHeight;
  if (elementBottom > scrollBottom) {
    element.scrollTop = elementBottom - element.clientHeight;
  } else if (element.offsetTop < element.scrollTop) {
    element.scrollTop = element.offsetTop;
  }
  element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
};

// get an updated option index after performing an action
export function getUpdatedIndex(currentIndex: number, maxIndex: number, action: SelectActions) {
  const pageSize = 10; // used for pageup/pagedown

  switch (action) {
    case SelectActions.First:
      return 0;
    case SelectActions.Last:
      return maxIndex;
    case SelectActions.Previous:
      return Math.max(0, currentIndex - 1);
    case SelectActions.Next:
      return Math.min(maxIndex, currentIndex + 1);
    case SelectActions.PageUp:
      return Math.max(0, currentIndex - pageSize);
    case SelectActions.PageDown:
      return Math.min(maxIndex, currentIndex + pageSize);
    default:
      return currentIndex;
  }
}

// check if element is visible in browser view port
export function isElementInView(element: HTMLElement) {
  var bounding = element.getBoundingClientRect();

  return (
    bounding.top >= 0 &&
    bounding.left >= 0 &&
    bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    bounding.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

// return the index of an option from an array of options, based on a search string
// if the filter is multiple iterations of the same letter (e.g "aaa"), then cycle through first-letter matches
export function getIndexByLetter(options: readonly Option[], filter: string, startIndex = 0) {
  const orderedOptions = [...options.slice(startIndex), ...options.slice(0, startIndex)];
  const firstMatch = filterOptions(orderedOptions, filter)[0];
  const allSameLetter = (array: string[]) => array.every((letter) => letter === array[0]);

  // first check if there is an exact match for the typed string
  if (firstMatch) {
    return options.indexOf(firstMatch);
  }

  // if the same letter is being repeated, cycle through first-letter matches
  else if (allSameLetter(filter.split(''))) {
    const matches = filterOptions(orderedOptions, filter[0]);
    return options.indexOf(matches[0]);
  }

  // if no matches, return -1
  else {
    return -1;
  }
}

// filter an array of options against an input string
// returns an array of options that begin with the filter string, case-independent
function filterOptions(options = [] as Option[], filter: string, exclude = [] as Option[]) {
  return options.filter((option) => {
    const matches = option.name.toLowerCase().indexOf(filter.toLowerCase()) === 0;
    return matches && exclude.indexOf(option) < 0;
  });
}

export function removeQueryParams(stateUrl:string) {
  const url = new URL(stateUrl, window.location.origin);
  url.searchParams.delete('Sheba-Preview');
  url.searchParams.delete('DocumentGuid');
  return url.pathname + url.search;
}

export const toLowerFirst = (str: string) =>
  str ? str.charAt(0).toLowerCase() + str.slice(1) : '';